/**
 * Auth token store.
 *
 * Holds the JWT issued by the dev IDP (or Auth0 in prod). Persisted to
 * localStorage so reloads restore the session. Expiry is tracked so we
 * proactively clear stale tokens instead of getting a 401 on the next
 * request.
 *
 * Single-tab semantics — the OIDC slice doesn't yet handle refresh
 * tokens or silent re-auth. When the token expires, the user goes back
 * to /login.
 */

import { create } from 'zustand';
import { setSentryUser } from './sentry.js';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'dispatcher' | 'office' | 'field_tech';
}

interface AuthState {
  token: string | null;
  expiresAt: number | null;
  user: AuthUser | null;

  setSession(input: { token: string; expiresIn: number; user: AuthUser }): void;
  clear(): void;
  /** True if we have a token AND it hasn't expired. */
  isAuthenticated(): boolean;
}

const TOKEN_KEY = 'tinsmith:auth:token';
const EXPIRES_KEY = 'tinsmith:auth:expiresAt';
const USER_KEY = 'tinsmith:auth:user';

function load(): { token: string | null; expiresAt: number | null; user: AuthUser | null } {
  if (typeof window === 'undefined') return { token: null, expiresAt: null, user: null };
  try {
    const token = window.localStorage.getItem(TOKEN_KEY);
    const expiresAtRaw = window.localStorage.getItem(EXPIRES_KEY);
    const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : null;
    const userRaw = window.localStorage.getItem(USER_KEY);
    const user = userRaw ? (JSON.parse(userRaw) as AuthUser) : null;
    // Drop expired sessions on load so we don't try to use them.
    if (token && expiresAt && expiresAt < Date.now()) {
      window.localStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(EXPIRES_KEY);
      window.localStorage.removeItem(USER_KEY);
      return { token: null, expiresAt: null, user: null };
    }
    return { token, expiresAt, user };
  } catch {
    return { token: null, expiresAt: null, user: null };
  }
}

function persist(state: { token: string | null; expiresAt: number | null; user: AuthUser | null }): void {
  if (typeof window === 'undefined') return;
  try {
    if (state.token) {
      window.localStorage.setItem(TOKEN_KEY, state.token);
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
    }
    if (state.expiresAt !== null) {
      window.localStorage.setItem(EXPIRES_KEY, String(state.expiresAt));
    } else {
      window.localStorage.removeItem(EXPIRES_KEY);
    }
    if (state.user) {
      window.localStorage.setItem(USER_KEY, JSON.stringify(state.user));
    } else {
      window.localStorage.removeItem(USER_KEY);
    }
  } catch {
    /* private mode — ignore */
  }
}

const initial = load();

export const useAuth = create<AuthState>((set, get) => ({
  token: initial.token,
  expiresAt: initial.expiresAt,
  user: initial.user,

  setSession({ token, expiresIn, user }) {
    const expiresAt = Date.now() + expiresIn * 1000;
    set({ token, expiresAt, user });
    persist({ token, expiresAt, user });
    setSentryUser({ id: user.id, role: user.role });
  },
  clear() {
    set({ token: null, expiresAt: null, user: null });
    persist({ token: null, expiresAt: null, user: null });
    setSentryUser(null);
  },
  isAuthenticated() {
    const { token, expiresAt } = get();
    if (!token) return false;
    if (expiresAt && expiresAt < Date.now()) return false;
    return true;
  },
}));

/**
 * Module-level synchronous accessor for the request layer.
 * Reads the live store snapshot so api.ts doesn't have to be a hook.
 */
export function currentAccessToken(): string | null {
  const { token, expiresAt } = useAuth.getState();
  if (!token) return null;
  if (expiresAt && expiresAt < Date.now()) return null;
  return token;
}
